import { datadogRum } from '@datadog/browser-rum'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToken } from '@omnea-app/fe-auth/TokenProvider'
import { getUploadFailureMessage } from './getUploadFailureMessage'
import { S3UploadFailure, uploadFilesToS3 } from './uploadFilesToS3'

/**
 * Rejects URL-shaped text so a failure message quoting the presigned URL cannot carry its path or query
 * string through. `:` is permitted so Chrome's `net::ERR_*` suffix survives — on its own it cannot form a
 * URL, since `/ ? & = %` stay excluded. It does not make a message safe in general: a credential value is
 * plain `\w` and would pass. Only the browser-authored messages on `network` and `unknown` reach here.
 */
const SAFE_TELEMETRY_MESSAGE = /^[\w .,:'()-]{1,200}$/

const toSafeMessage = (message: string): string => (SAFE_TELEMETRY_MESSAGE.test(message) ? message : 'redacted')

/** Nothing derived from the filename is reported. */
const toFailureTelemetry = (failures: S3UploadFailure[]) =>
  failures.map((failure) => ({
    kind: failure.kind,
    fileSizeBytes: failure.fileSizeBytes,
    ...(failure.kind === 'http' ? { status: failure.status } : {}),
    ...(failure.kind === 'timeout' ? { timeoutMs: failure.timeoutMs } : {}),
    ...('message' in failure ? { message: toSafeMessage(failure.message) } : {}),
  }))

export const useUploadFiles = () => {
  const { getToken } = useToken()
  const { t } = useTranslation('documents')
  const [isUploadingDocument, setIsUploadingDocument] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const clearUploadError = () => setUploadError(null)

  const uploadDocument = async (documents: File[]) => {
    setIsUploadingDocument(true)
    setUploadError(null)

    const documentsToUpload = documents.filter((document) => document.size > 0)
    if (documentsToUpload.length !== documents.length) {
      enqueueSnackbar(t('upload-document-warning', { documentCount: documents.length - documentsToUpload.length }), {
        variant: 'warning',
      })
    }

    try {
      const token = await getToken()
      const { uploaded, failedFileIndices, failures } = await uploadFilesToS3(documentsToUpload, token)

      if (failedFileIndices.size > 0) {
        const failureMessage = getUploadFailureMessage(failures, t)
        setUploadError(failureMessage)
        enqueueSnackbar(failureMessage, { variant: 'error' })
        datadogRum.addError(new Error('S3 upload failed for one or more files'), {
          messages: ['Document Upload to S3'],
          failures: toFailureTelemetry(failures),
        })
        return null
      }

      return uploaded
    } catch (error) {
      setUploadError(t('upload-document-error'))
      enqueueSnackbar(t('upload-document-error'), { variant: 'error' })
      datadogRum.addError(error, { messages: ['Document Upload to S3'] })
      return null
    } finally {
      setIsUploadingDocument(false)
    }
  }

  return { uploadDocument, isUploadingDocument, uploadError, clearUploadError }
}
